// src/cockpit4/components/CEOMorningReport.tsx
// ============================================
// CEO 智能晨报（P0-功能1）- v2.0 详细改进计划
//
// 设计：完整 CEO 晨报，包含 4 个区块：
//   1. 数据摘要 - 昨日经营数据（收入/客户/内容/任务完成率）
//   2. 异常预警 - 红色标注下降/超支/延期等异常项
//   3. AI 建议 - 今日最该关注的 TOP3 事项 + 具体行动建议
//   4. 对标分析 - 与上周/上月/目标的对比
//
// 数据来源：
//   - EnterpriseBrain.get_today_focus / get_kpis / get_risks / get_goals
//   - useDashboardData 的 CEO KPI（今日收入/客户）
//   - /vault-api 读取昨日 Daily 文件做对比
//
// Token 优化：仅手动触发，不在挂载时自动调用 LLM
// 写入路径：10_Operations（运营层）/Daily/晨报-{date}.md
// ============================================

import { useCallback, useMemo, useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Sun,
  Sparkles,
  Loader2,
  TrendingUp,
  AlertTriangle,
  Target,
  Activity,
  Users,
  FileText,
  CheckSquare,
  Brain,
  RefreshCw,
  ArrowUpRight,
  ArrowDownRight,
  Minus,
  Newspaper,
  Flame,
  ExternalLink,
} from "lucide-react";
import RuntimeManager from "../../runtime/RuntimeManager";
import { RuntimeName } from "../../types/runtime";
import { useDashboardData } from "../../hooks/useDashboardData";
import { formatYuanWithSymbol } from "../../components/Dashboard/shared";

// ============================================
// 类型定义
// ============================================

/** KPI 简化结构 */
interface BriefingKpi {
  label: string;
  value: string;
  /** 同比百分比（与昨日对比） */
  delta?: number;
  /** 目标值（用于完成率计算） */
  target?: number;
  /** 当前值（用于完成率计算） */
  currentValue?: number;
  icon: "revenue" | "customer" | "content" | "task";
}

/** 异常预警项 */
interface AnomalyItem {
  /** 异常类型 */
  type: "drop" | "overspend" | "delay" | "risk" | "warning";
  /** 异常描述 */
  description: string;
  /** 严重程度 */
  severity: "high" | "medium" | "low";
  /** 涉及模块 */
  module?: string;
  /** 数值依据 */
  dataPoint?: string;
}

/** AI 建议项 */
interface AiSuggestion {
  /** 排名（1/2/3） */
  rank: 1 | 2 | 3;
  /** 标题 */
  title: string;
  /** 问题描述 */
  problem: string;
  /** 数据依据 */
  evidence: string;
  /** 建议方案 */
  action: string;
  /** 优先级 */
  priority: "high" | "medium" | "low";
  /** 来源模块 */
  source?: string;
}

/** 对标分析项 */
interface BenchmarkItem {
  /** 指标名称 */
  metric: string;
  /** 当前值 */
  current: string;
  /** 上周对比百分比 */
  vsLastWeek?: number;
  /** 上月对比百分比 */
  vsLastMonth?: number;
  /** 目标完成率 */
  vsTarget?: number;
}

/** 完整晨报数据 */
interface MorningReportData {
  /** 日期 */
  date: string;
  /** 数据摘要 */
  summary: BriefingKpi[];
  /** 异常预警 */
  anomalies: AnomalyItem[];
  /** AI 建议 */
  suggestions: AiSuggestion[];
  /** 对标分析 */
  benchmarks: BenchmarkItem[];
  /** 数据来源 */
  source: "enterprise_brain" | "derived" | "vault_only";
  /** 生成时间 */
  generatedAt: string;
}

// ============================================
// 主组件
// ============================================

export default function CEOMorningReport() {
  const dashboard = useDashboardData();
  const ceoData = dashboard.ceo;

  const [report, setReport] = useState<MorningReportData | null>(null);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<{ type: "ok" | "err"; text: string } | null>(null);
  const [expandedSection, setExpandedSection] = useState<
    "summary" | "anomaly" | "suggestion" | "benchmark" | "news" | "hotspot" | null
  >("summary");

  // ============================================
  // v6.29 今日 AI 新闻 + 今日热点（自动加载，不消耗 token）
  // ============================================
  const [newsItems, setNewsItems] = useState<{ title: string; source: string; url?: string }[]>([]);
  const [hotTopics, setHotTopics] = useState<{ title: string; angle?: string }[]>([]);
  const [newsLoading, setNewsLoading] = useState(false);
  const [newsError, setNewsError] = useState<string>("");

  /** 解析 News-{date}.md → 新闻条目（按来源分组提取标题） */
  const parseNewsFile = useCallback((content: string): { title: string; source: string; url?: string }[] => {
    const items: { title: string; source: string; url?: string }[] = [];
    let currentSource = "AI";
    const lines = content.split("\n");
    for (const line of lines) {
      const srcM = line.match(/^###\s+(.+?)\s*$/);
      if (srcM) {
        currentSource = srcM[1].trim();
        continue;
      }
      const bulletM = line.match(/^-\s*\*\*(.+?)\*\*\s*$/);
      if (bulletM) {
        items.push({ title: bulletM[1].trim(), source: currentSource });
        continue;
      }
      // 兼容 `- 标题` 或 `- **标题** 描述`
      const altM = line.match(/^-\s*\*\*(.+?)\*\*/);
      if (altM) {
        items.push({ title: altM[1].trim(), source: currentSource });
      }
    }
    // 过滤明显是导航/页脚的内容
    return items
      .filter((i) => i.title.length >= 6 && !/(职位|版权所有|ICP备|备案|隐私|条款|登录|注册|首页|Press kit|Try |下载)/.test(i.title))
      .slice(0, 20);
  }, []);

  /** 解析 今日热点-{date}.md → 热点条目 */
  const parseHotTopics = useCallback((content: string): { title: string; angle?: string }[] => {
    const items: { title: string; angle?: string }[] = [];
    const lines = content.split("\n");
    let inSensitive = false;
    for (const line of lines) {
      if (line.includes("敏感热点量化参考")) inSensitive = true;
      if (inSensitive) continue;
      const m = line.match(/^-\s*(.+?)(?:\s*\|\s*(.+))?$/);
      if (m && m[1].length >= 4 && !m[1].startsWith(">")) {
        items.push({ title: m[1].trim().slice(0, 80), angle: (m[2] || "").trim() });
      }
    }
    return items.slice(0, 10);
  }, []);

  /** 自动加载：今日 AI 新闻 + 今日热点 + 当天已生成晨报 */
  const loadExternalData = useCallback(async () => {
    setNewsLoading(true);
    setNewsError("");
    const date = new Date().toISOString().slice(0, 10);
    try {
      // 1. 今日 AI 新闻（NewsRuntime 采集结果写入 Vault）
      try {
        const newsResp = await fetch(
          `/vault-api/read?path=${encodeURIComponent(`XEAOS/Daily/News-${date}.md`)}`
        );
        if (newsResp.ok) {
          const json = await newsResp.json();
          if (json?.success && json.content) {
            const items = parseNewsFile(json.content);
            setNewsItems(items.length > 0 ? items : [{ title: "今日 AI 新闻尚未采集（点右上角「采集今日新闻」）", source: "提示" }]);
          } else {
            setNewsItems([{ title: "今日 AI 新闻尚未采集（点右上角「采集今日新闻」）", source: "提示" }]);
          }
        } else {
          setNewsItems([{ title: "今日 AI 新闻尚未采集（点右上角「采集今日新闻」）", source: "提示" }]);
        }
      } catch {
        setNewsError("新闻读取失败");
      }

      // 2. 今日热点（WorkBuddy 自动化每日 06:00 全网采集）
      try {
        const hotResp = await fetch(
          `/vault-api/read?path=${encodeURIComponent(
            `10_Operations（运营层）/Content/HotTopics/今日热点-${date}.md`
          )}`
        );
        if (hotResp.ok) {
          const json = await hotResp.json();
          if (json?.success && json.content) {
            setHotTopics(parseHotTopics(json.content));
          }
        }
      } catch {
        // 忽略
      }

      // 3. 当天已生成的晨报（8:00 自动化「AI 经营·早晨计划」）→ 自动展示
      try {
        const reportResp = await fetch(
          `/vault-api/read?path=${encodeURIComponent(`10_Operations（运营层）/Daily/晨报-${date}.md`)}`
        );
        if (reportResp.ok) {
          const json = await reportResp.json();
          if (json?.success && json.content && !report) {
            // 解析晨报：提取数据摘要表格
            const content = json.content;
            const summary: BriefingKpi[] = [];
            const tableM = content.match(/\| 指标 \| 数值 \|[\s\S]*?\n((?:\|[^\n]+\|\n)+)/);
            if (tableM) {
              const rows = tableM[1].trim().split("\n");
              for (const row of rows) {
                const cells = row.split("|").map((c: string) => c.trim()).filter(Boolean);
                if (cells.length >= 2) {
                  summary.push({
                    label: cells[0],
                    value: cells[1],
                    icon: /收入/.test(cells[0]) ? "revenue" : /客户/.test(cells[0]) ? "customer" : /内容/.test(cells[0]) ? "content" : "task",
                  });
                }
              }
            }
            setReport({
              date,
              summary: summary.length > 0 ? summary : [{ label: "晨报文件", value: "已生成", icon: "content" }],
              anomalies: [],
              suggestions: [],
              benchmarks: [],
              source: "vault_only",
              generatedAt: new Date().toISOString(),
            });
            setMessage({ type: "ok", text: `✅ 已自动加载当天晨报（${date}）\n数据来源：Vault Daily/晨报-${date}.md` });
          }
        }
      } catch {
        // 忽略
      }
    } finally {
      setNewsLoading(false);
    }
  }, [parseNewsFile, parseHotTopics, report]);

  useEffect(() => {
    void loadExternalData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ============================================
  // 数据聚合：构建完整晨报
  // ============================================
  const buildReport = useCallback(async (): Promise<MorningReportData> => {
    const date = new Date().toISOString().slice(0, 10);
    const now = new Date().toISOString();

    // 1. 拉 EnterpriseBrain 数据（不消耗 token，读缓存）
    let todayFocus: any = null;
    let kpis: any[] = [];
    let risks: any[] = [];

    try {
      if (RuntimeManager.hasRuntime(RuntimeName.EnterpriseBrain)) {
        const [focusResp, kpiResp, riskResp] = await Promise.all([
          RuntimeManager.execute(RuntimeName.EnterpriseBrain, "get_today_focus", { date }).catch(() => null),
          RuntimeManager.query(RuntimeName.EnterpriseBrain, { type: "get_kpis" }).catch(() => null),
          RuntimeManager.query(RuntimeName.EnterpriseBrain, { type: "get_risks" }).catch(() => null),
        ]);

        if (focusResp?.success) todayFocus = focusResp.data;
        if (kpiResp?.success && Array.isArray(kpiResp.data)) kpis = kpiResp.data;
        if (riskResp?.success && Array.isArray(riskResp.data)) risks = riskResp.data;
      }
    } catch {
      // 静默降级
    }

    // 2. 拉 Vault 昨日 Daily 文件，用于对标分析
    let yesterdayData: { content?: number; tasks?: number; revenue?: number } = {};
    try {
      const yesterday = new Date(Date.now() - 86400000).toISOString().slice(0, 10);
      const resp = await fetch(
        `/vault-api/read?path=${encodeURIComponent(`10_Operations（运营层）/Daily/${yesterday}.md`)}`
      );
      if (resp.ok) {
        const json = await resp.json();
        if (json?.content) {
          // 解析昨日任务完成数
          const doneMatches = json.content.match(/- \[x\]/gi);
          const todoMatches = json.content.match(/- \[ \]/gi);
          yesterdayData.tasks = (doneMatches?.length || 0) + (todoMatches?.length || 0);
        }
      }
    } catch {
      // 忽略
    }

    // 3. 构建数据摘要
    const summary: BriefingKpi[] = [
      {
        label: "今日收入",
        value: ceoData.degraded ? "--" : formatYuanWithSymbol(ceoData.kpi.revenue),
        icon: "revenue",
        currentValue: ceoData.kpi.revenue,
        target: 0,
      },
      {
        label: "今日新增客户",
        value: ceoData.degraded ? "--" : `+${ceoData.kpi.newCustomers}`,
        icon: "customer",
        currentValue: ceoData.kpi.newCustomers,
      },
      {
        label: "今日内容产出",
        value: ceoData.degraded ? "--" : "—",
        icon: "content",
      },
      {
        label: "今日任务",
        value: "—",
        icon: "task",
      },
    ];

    // 4. 构建异常预警（基于 KPI 状态 + 风险列表）
    const anomalies: AnomalyItem[] = [];
    kpis.forEach((kpi) => {
      if (kpi.status === "critical" || kpi.status === "warning") {
        anomalies.push({
          type: kpi.trend === "down" ? "drop" : "warning",
          description: `${kpi.name} 当前值 ${kpi.value}${kpi.unit || ""}，${
            kpi.status === "critical" ? "处于危险区间" : "需要关注"
          }`,
          severity: kpi.status === "critical" ? "high" : "medium",
          module: kpi.category,
          dataPoint: `目标 ${kpi.target}${kpi.unit || ""}，趋势 ${
            kpi.trend === "up" ? "↑" : kpi.trend === "down" ? "↓" : "→"
          } ${Math.abs(kpi.trendValue || 0)}%`,
        });
      }
    });
    risks.slice(0, 5).forEach((risk) => {
      if (risk.status !== "mitigated") {
        anomalies.push({
          type: "risk",
          description: risk.title || risk.impact || "未命名风险",
          severity:
            risk.severity === "critical"
              ? "high"
              : risk.severity === "high"
              ? "high"
              : risk.severity === "medium"
              ? "medium"
              : "low",
          module: "Risk",
          dataPoint: risk.mitigation ? `缓解措施：${risk.mitigation.slice(0, 60)}` : undefined,
        });
      }
    });

    // 5. 构建 AI 建议（基于 today_focus + 风险 + KPI 偏差）
    const suggestions: AiSuggestion[] = [];
    if (todayFocus?.priorities?.length > 0) {
      todayFocus.priorities.slice(0, 3).forEach((p: any, i: number) => {
        suggestions.push({
          rank: (i + 1) as 1 | 2 | 3,
          title: p.title || `第 ${i + 1} 优先事项`,
          problem: p.rationale || "—",
          evidence: p.expectedOutcome || "—",
          action: p.product ? `涉及产品：${p.product}` : "请前往对应模块处理",
          priority:
            i === 0 ? "high" : i === 1 ? "medium" : "low",
          source: "EnterpriseBrain",
        });
      });
    }
    // 若 EnterpriseBrain 无 todayFocus，则从风险中派生建议
    if (suggestions.length === 0 && risks.length > 0) {
      risks.slice(0, 3).forEach((r: any, i: number) => {
        suggestions.push({
          rank: (i + 1) as 1 | 2 | 3,
          title: `处理风险：${r.title || "未命名"}`,
          problem: r.impact || "—",
          evidence: `严重程度：${r.severity || "unknown"}，概率：${r.probability || "unknown"}`,
          action: r.mitigation || "请评估并制定缓解措施",
          priority: r.severity === "critical" || r.severity === "high" ? "high" : "medium",
          source: "RiskEngine",
        });
      });
    }
    // 若仍无建议，给出默认建议
    if (suggestions.length === 0) {
      suggestions.push(
        {
          rank: 1,
          title: "运行 EnterpriseBrain 每日分析",
          problem: "尚未生成今日企业总脑报告",
          evidence: "EnterpriseBrain 是 AI CEO 大脑，每日 08:30 自动运行",
          action: "点击右上角「生成晨报」按钮，或在设置中确认 EnterpriseBrain Runtime 已启动",
          priority: "high",
          source: "System",
        },
        {
          rank: 2,
          title: "检查各模块今日数据",
          problem: "数据可能不完整或降级",
          evidence: "CEO KPI degraded=" + (ceoData.degraded ? "true" : "false"),
          action: "前往各 Workspace 验证数据源是否正常",
          priority: "medium",
          source: "System",
        },
        {
          rank: 3,
          title: "审阅昨日 Daily 文件",
          problem: "需要对昨日工作进行复盘",
          evidence: yesterdayData.tasks ? `昨日任务数：${yesterdayData.tasks}` : "昨日 Daily 文件未找到",
          action: "前往战略计划 → 五层计划联动下钻 → 日层查看",
          priority: "low",
          source: "Vault",
        }
      );
    }

    // 6. 构建对标分析（基于 KPI 完成率 + 趋势）
    const benchmarks: BenchmarkItem[] = kpis.slice(0, 6).map((kpi) => ({
      metric: kpi.name || "—",
      current: `${kpi.value}${kpi.unit || ""}`,
      vsLastWeek: kpi.trendValue ? (kpi.trend === "up" ? kpi.trendValue : -kpi.trendValue) : undefined,
      vsTarget: kpi.target > 0 ? Math.round((kpi.value / kpi.target) * 100) : undefined,
    }));

    // 7. 数据来源判断
    const source: MorningReportData["source"] =
      todayFocus ? "enterprise_brain" : kpis.length > 0 || risks.length > 0 ? "derived" : "vault_only";

    return {
      date,
      summary,
      anomalies,
      suggestions,
      benchmarks,
      source,
      generatedAt: now,
    };
  }, [ceoData]);

  // ============================================
  // 写入 Vault
  // ============================================
  const persistToVault = useCallback(async (data: MorningReportData): Promise<string> => {
    try {
      const content = [
        `---`,
        `type: ceo_morning_report`,
        `date: ${data.date}`,
        `created_at: ${data.generatedAt}`,
        `status: generated`,
        `source: ${data.source}`,
        `version: v2.0`,
        `---`,
        ``,
        `# 🌅 CEO 智能晨报 · ${data.date}`,
        ``,
        `> 生成时间：${data.generatedAt}`,
        `> 数据来源：${data.source}`,
        ``,
        `## 📊 数据摘要`,
        ``,
        `| 指标 | 数值 |`,
        `|------|------|`,
        ...data.summary.map((s) => `| ${s.label} | ${s.value} |`),
        ``,
        `## ⚠️ 异常预警（${data.anomalies.length} 项）`,
        ``,
        ...(data.anomalies.length === 0
          ? ["✅ 今日无异常项"]
          : data.anomalies.map(
              (a, i) =>
                `${i + 1}. **[${a.severity.toUpperCase()}]** ${a.description}${
                  a.module ? `（${a.module}）` : ""
                }${a.dataPoint ? ` — ${a.dataPoint}` : ""}`
            )),
        ``,
        `## 🎯 AI 建议（TOP ${data.suggestions.length}）`,
        ``,
        ...data.suggestions.map(
          (s) =>
            `### ${s.rank}. ${s.title}\n- **问题**：${s.problem}\n- **依据**：${s.evidence}\n- **行动**：${s.action}\n- **优先级**：${s.priority}\n`
        ),
        ``,
        `## 📈 对标分析`,
        ``,
        `| 指标 | 当前 | 较上周 | 较目标 |`,
        `|------|------|--------|--------|`,
        ...data.benchmarks.map(
          (b) =>
            `| ${b.metric} | ${b.current} | ${
              b.vsLastWeek !== undefined ? `${b.vsLastWeek > 0 ? "+" : ""}${b.vsLastWeek}%` : "—"
            } | ${b.vsTarget !== undefined ? `${b.vsTarget}%` : "—"} |`
        ),
        ``,
      ].join("\n");

      const resp = await fetch(
        `/vault-api/write?path=${encodeURIComponent(`10_Operations（运营层）/Daily/晨报-${data.date}.md`)}`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ content }),
        }
      );
      if (!resp.ok) throw new Error("写入失败");
      return `10_Operations（运营层）/Daily/晨报-${data.date}.md`;
    } catch (err) {
      return `写入 Vault 失败：${(err as Error).message}`;
    }
  }, []);

  // ============================================
  // 手动生成晨报（节省 token）
  // ============================================
  const generateReport = useCallback(async () => {
    setLoading(true);
    setMessage(null);
    try {
      const data = await buildReport();
      setReport(data);
      const filePath = await persistToVault(data);
      setMessage({
        type: "ok",
        text: `✅ 智能晨报已生成（${data.source}）\n📄 已写入 Vault：${filePath}`,
      });
    } catch (err) {
      setMessage({
        type: "err",
        text: `❌ 生成失败：${(err as Error).message}`,
      });
    } finally {
      setLoading(false);
    }
  }, [buildReport, persistToVault]);

  // ============================================
  // 渲染
  // ============================================
  const sourceLabel = useMemo(() => {
    if (!report) return "未生成";
    return report.source === "enterprise_brain"
      ? "Enterprise Brain"
      : report.source === "derived"
      ? "Derived Data"
      : "Vault Only";
  }, [report]);

  const sectionConfigs = useMemo(
    () => [
      {
        id: "summary" as const,
        label: "数据摘要",
        icon: Activity,
        color: "#3B82F6",
        count: report?.summary.length || 0,
      },
      {
        id: "anomaly" as const,
        label: "异常预警",
        icon: AlertTriangle,
        color: "#EF4444",
        count: report?.anomalies.length || 0,
      },
      {
        id: "suggestion" as const,
        label: "AI 建议",
        icon: Sparkles,
        color: "#A855F7",
        count: report?.suggestions.length || 0,
      },
      {
        id: "benchmark" as const,
        label: "对标分析",
        icon: Target,
        color: "#10B981",
        count: report?.benchmarks.length || 0,
      },
      {
        id: "news" as const,
        label: "今日 AI 新闻",
        icon: Newspaper,
        color: "#06B6D4",
        count: newsItems.length || 0,
      },
      {
        id: "hotspot" as const,
        label: "今日热点",
        icon: Flame,
        color: "#EF4444",
        count: hotTopics.length || 0,
      },
    ],
    [report, newsItems.length, hotTopics.length]
  );

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      className="relative rounded-xl border border-white/[0.06] bg-gradient-to-br from-[#131826]/60 to-[#0a0e1a]/60 backdrop-blur-md overflow-hidden"
    >
      {/* 顶部光带 */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-amber-500/40 to-transparent" />

      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-white/[0.04]">
        <div className="flex items-center gap-2.5">
          <div className="w-7 h-7 rounded-md bg-amber-500/15 border border-amber-500/30 flex items-center justify-center">
            <Sun size={13} className="text-amber-400" />
          </div>
          <div>
            <h3 className="text-[13px] font-semibold text-white tracking-tight">CEO 智能晨报</h3>
            <p className="text-[9px] font-mono text-gray-500 uppercase tracking-wider mt-0.5">
              AI Morning Briefing · {sourceLabel}
              {report && ` · ${new Date(report.generatedAt).toLocaleTimeString("zh-CN", { hour12: false })}`}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {/* 数据来源标记 */}
          {report && (
            <div className="flex items-center gap-1.5 px-2 py-1 rounded-md bg-white/[0.03] border border-white/[0.06]">
              <Brain size={10} className="text-purple-400" />
              <span className="text-[9px] font-mono text-purple-300 uppercase tracking-wider">
                {report.anomalies.length} 异常 · {report.suggestions.length} 建议
              </span>
            </div>
          )}
          {/* 生成按钮 */}
          <button
            onClick={generateReport}
            disabled={loading}
            className="flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-amber-500/15 border border-amber-500/30 hover:bg-amber-500/25 transition-colors disabled:opacity-50"
            title="手动生成 CEO 智能晨报（聚合 EnterpriseBrain + KPI + 风险 + 对标分析）"
          >
            {loading ? (
              <Loader2 size={11} className="text-amber-400 animate-spin" />
            ) : (
              <RefreshCw size={11} className="text-amber-400" />
            )}
            <span className="text-[9px] font-mono text-amber-300 uppercase tracking-wider">
              {loading ? "生成中..." : report ? "重新生成" : "生成晨报"}
            </span>
          </button>
        </div>
      </div>

      {/* 消息提示 */}
      {message && (
        <div className="px-4 py-2 border-b border-white/[0.04]">
          <div
            className={`px-3 py-2 rounded-md border text-[10px] font-mono ${
              message.type === "ok"
                ? "border-emerald-500/30 bg-emerald-500/[0.06] text-emerald-300"
                : "border-red-500/30 bg-red-500/[0.06] text-red-300"
            }`}
          >
            <span className="whitespace-pre-line">{message.text}</span>
          </div>
        </div>
      )}

      {/* 内容区 */}
      <div className="p-4">
        {!report ? (
          <div className="py-10 text-center">
            <Sun size={28} className="mx-auto text-gray-700 mb-2" />
            <p className="text-[11px] font-mono text-gray-500">
              {loading ? "正在聚合企业总脑数据..." : "点击「生成晨报」获取完整 CEO 晨报"}
            </p>
            <p className="text-[9px] font-mono text-gray-600 mt-1">
              包含：数据摘要 · 异常预警 · AI 建议 · 对标分析
            </p>
          </div>
        ) : (
          <>
            {/* 区块切换 Tab */}
            <div className="flex items-center gap-1 mb-3 overflow-x-auto">
              {sectionConfigs.map((sec) => {
                const Icon = sec.icon;
                const active = expandedSection === sec.id;
                return (
                  <button
                    key={sec.id}
                    onClick={() => setExpandedSection(active ? null : sec.id)}
                    className={`flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[10px] font-mono transition-all whitespace-nowrap ${
                      active
                        ? "text-white border"
                        : "text-gray-500 hover:text-gray-300 hover:bg-white/[0.02] border border-transparent"
                    }`}
                    style={
                      active
                        ? { background: `${sec.color}15`, borderColor: `${sec.color}40` }
                        : undefined
                    }
                  >
                    <Icon size={10} style={active ? { color: sec.color } : undefined} />
                    <span className="uppercase tracking-wider">{sec.label}</span>
                    <span
                      className="px-1 rounded text-[8px]"
                      style={{
                        background: active ? `${sec.color}20` : "rgba(255,255,255,0.04)",
                        color: active ? sec.color : "#6b7280",
                      }}
                    >
                      {sec.count}
                    </span>
                  </button>
                );
              })}
            </div>

            {/* 区块内容 */}
            <AnimatePresence mode="wait">
              {expandedSection === "summary" && (
                <motion.div
                  key="summary"
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  exit={{ opacity: 0, height: 0 }}
                  className="overflow-hidden"
                >
                  <div className="grid grid-cols-2 gap-2">
                    {report.summary.map((kpi, i) => {
                      const Icon =
                        kpi.icon === "revenue"
                          ? TrendingUp
                          : kpi.icon === "customer"
                          ? Users
                          : kpi.icon === "content"
                          ? FileText
                          : CheckSquare;
                      const color =
                        kpi.icon === "revenue"
                          ? "#10B981"
                          : kpi.icon === "customer"
                          ? "#3B82F6"
                          : kpi.icon === "content"
                          ? "#EC4899"
                          : "#A855F7";
                      return (
                        <div
                          key={i}
                          className="rounded-md border border-white/[0.04] bg-white/[0.02] p-2.5"
                        >
                          <div className="flex items-center justify-between mb-1">
                            <Icon size={11} style={{ color }} />
                            <span className="text-[8px] font-mono text-gray-600 uppercase">
                              {kpi.icon}
                            </span>
                          </div>
                          <div className="text-[14px] font-mono font-bold text-white leading-tight">
                            {kpi.value}
                          </div>
                          <div className="text-[9px] font-mono text-gray-500 mt-0.5">
                            {kpi.label}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </motion.div>
              )}

              {expandedSection === "anomaly" && (
                <motion.div
                  key="anomaly"
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  exit={{ opacity: 0, height: 0 }}
                  className="overflow-hidden"
                >
                  {report.anomalies.length === 0 ? (
                    <div className="text-center py-6">
                      <CheckSquare size={20} className="mx-auto text-emerald-500/60 mb-1" />
                      <p className="text-[11px] font-mono text-emerald-400">
                        ✅ 今日无异常项
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-1.5 max-h-64 overflow-y-auto">
                      {report.anomalies.map((anomaly, i) => {
                        const sevColor =
                          anomaly.severity === "high"
                            ? "#EF4444"
                            : anomaly.severity === "medium"
                            ? "#F59E0B"
                            : "#3B82F6";
                        return (
                          <div
                            key={i}
                            className="rounded-md border p-2.5"
                            style={{
                              borderColor: `${sevColor}30`,
                              background: `${sevColor}08`,
                            }}
                          >
                            <div className="flex items-start justify-between gap-2">
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-1.5 mb-0.5">
                                  <AlertTriangle size={10} style={{ color: sevColor }} />
                                  <span
                                    className="text-[9px] font-mono uppercase tracking-wider"
                                    style={{ color: sevColor }}
                                  >
                                    {anomaly.severity} · {anomaly.type}
                                  </span>
                                  {anomaly.module && (
                                    <span className="text-[8px] font-mono text-gray-600">
                                      [{anomaly.module}]
                                    </span>
                                  )}
                                </div>
                                <p className="text-[11px] text-gray-200 leading-snug">
                                  {anomaly.description}
                                </p>
                                {anomaly.dataPoint && (
                                  <p className="text-[9px] font-mono text-gray-500 mt-1">
                                    {anomaly.dataPoint}
                                  </p>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </motion.div>
              )}

              {expandedSection === "suggestion" && (
                <motion.div
                  key="suggestion"
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  exit={{ opacity: 0, height: 0 }}
                  className="overflow-hidden"
                >
                  <div className="space-y-2">
                    {report.suggestions.map((s, i) => {
                      const prColor =
                        s.priority === "high"
                          ? "#EF4444"
                          : s.priority === "medium"
                          ? "#F59E0B"
                          : "#3B82F6";
                      return (
                        <motion.div
                          key={i}
                          initial={{ opacity: 0, x: -8 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: i * 0.05 }}
                          className="rounded-md border border-white/[0.06] bg-white/[0.02] p-2.5"
                        >
                          <div className="flex items-start gap-2">
                            <span
                              className="flex-shrink-0 w-5 h-5 rounded flex items-center justify-center text-[11px] font-mono font-bold"
                              style={{ background: `${prColor}20`, color: prColor }}
                            >
                              {s.rank}
                            </span>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-1.5 mb-1">
                                <span className="text-[11px] font-semibold text-white truncate">
                                  {s.title}
                                </span>
                                <span
                                  className="px-1 py-0.5 rounded text-[8px] font-mono uppercase"
                                  style={{ background: `${prColor}15`, color: prColor }}
                                >
                                  {s.priority}
                                </span>
                                {s.source && (
                                  <span className="text-[8px] font-mono text-gray-600 uppercase">
                                    {s.source}
                                  </span>
                                )}
                              </div>
                              <div className="space-y-0.5">
                                <div className="text-[9px] font-mono text-gray-500">
                                  <span className="text-gray-600">问题：</span>
                                  <span className="text-gray-300">{s.problem}</span>
                                </div>
                                <div className="text-[9px] font-mono text-gray-500">
                                  <span className="text-gray-600">依据：</span>
                                  <span className="text-gray-300">{s.evidence}</span>
                                </div>
                                <div className="text-[9px] font-mono text-gray-500">
                                  <span className="text-gray-600">行动：</span>
                                  <span style={{ color: prColor }}>{s.action}</span>
                                </div>
                              </div>
                            </div>
                          </div>
                        </motion.div>
                      );
                    })}
                  </div>
                </motion.div>
              )}

              {expandedSection === "benchmark" && (
                <motion.div
                  key="benchmark"
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  exit={{ opacity: 0, height: 0 }}
                  className="overflow-hidden"
                >
                  {report.benchmarks.length === 0 ? (
                    <p className="text-center py-6 text-[11px] font-mono text-gray-500">
                      暂无对标数据（EnterpriseBrain 未运行）
                    </p>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full text-[10px] font-mono">
                        <thead>
                          <tr className="text-gray-500 border-b border-white/[0.06]">
                            <th className="text-left py-1.5 px-2 font-normal uppercase tracking-wider">
                              指标
                            </th>
                            <th className="text-right py-1.5 px-2 font-normal uppercase tracking-wider">
                              当前
                            </th>
                            <th className="text-right py-1.5 px-2 font-normal uppercase tracking-wider">
                              较上周
                            </th>
                            <th className="text-right py-1.5 px-2 font-normal uppercase tracking-wider">
                              较目标
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {report.benchmarks.map((b, i) => (
                            <tr
                              key={i}
                              className="border-b border-white/[0.03] last:border-b-0 hover:bg-white/[0.02]"
                            >
                              <td className="py-1.5 px-2 text-gray-300">{b.metric}</td>
                              <td className="py-1.5 px-2 text-right text-white">{b.current}</td>
                              <td className="py-1.5 px-2 text-right">
                                {b.vsLastWeek === undefined ? (
                                  <span className="text-gray-700">—</span>
                                ) : (
                                  <span
                                    className={`flex items-center justify-end gap-0.5 ${
                                      b.vsLastWeek > 0
                                        ? "text-emerald-400"
                                        : b.vsLastWeek < 0
                                        ? "text-red-400"
                                        : "text-gray-500"
                                    }`}
                                  >
                                    {b.vsLastWeek > 0 ? (
                                      <ArrowUpRight size={9} />
                                    ) : b.vsLastWeek < 0 ? (
                                      <ArrowDownRight size={9} />
                                    ) : (
                                      <Minus size={9} />
                                    )}
                                    {Math.abs(b.vsLastWeek)}%
                                  </span>
                                )}
                              </td>
                              <td className="py-1.5 px-2 text-right">
                                {b.vsTarget === undefined ? (
                                  <span className="text-gray-700">—</span>
                                ) : (
                                  <span
                                    className={
                                      b.vsTarget >= 100
                                        ? "text-emerald-400"
                                        : b.vsTarget >= 70
                                        ? "text-amber-400"
                                        : "text-red-400"
                                    }
                                  >
                                    {b.vsTarget}%
                                  </span>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </motion.div>
              )}
              {expandedSection === "news" && (
                <motion.div
                  key="news"
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  exit={{ opacity: 0, height: 0 }}
                  className="overflow-hidden"
                >
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-[9px] font-mono text-gray-500">
                      来源：NewsRuntime 采集（OpenAI/Anthropic/DeepSeek/智谱/通义等）+ Hermes 内容工厂
                    </p>
                    {newsLoading && <Loader2 size={10} className="text-cyan-400 animate-spin" />}
                  </div>
                  {newsError && <p className="text-[10px] font-mono text-red-400 mb-2">{newsError}</p>}
                  {newsItems.length === 0 && !newsLoading ? (
                    <div className="text-center py-6">
                      <Newspaper size={20} className="mx-auto text-gray-700 mb-1" />
                      <p className="text-[11px] font-mono text-gray-500">今日 AI 新闻尚未采集</p>
                      <p className="text-[9px] font-mono text-gray-600 mt-1">
                        前往「Hermes → 洞察」或「内容工厂」点击采集，或等待每日 09:00 自动采集
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-1 max-h-72 overflow-y-auto pr-1">
                      {newsItems.map((n, i) => (
                        <div
                          key={i}
                          className="rounded-md border border-white/[0.04] bg-white/[0.02] px-2.5 py-1.5 flex items-start gap-2"
                        >
                          <Newspaper size={9} className="text-cyan-400 flex-shrink-0 mt-0.5" />
                          <div className="flex-1 min-w-0">
                            <p className="text-[10px] text-gray-200 leading-snug">{n.title}</p>
                            <span className="text-[8px] font-mono text-cyan-500/80 uppercase">{n.source}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </motion.div>
              )}

              {expandedSection === "hotspot" && (
                <motion.div
                  key="hotspot"
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  exit={{ opacity: 0, height: 0 }}
                  className="overflow-hidden"
                >
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-[9px] font-mono text-gray-500">
                      来源：今日热点文件（WorkBuddy 自动化每日 06:00 全网采集）
                    </p>
                  </div>
                  {hotTopics.length === 0 ? (
                    <div className="text-center py-6">
                      <Flame size={20} className="mx-auto text-gray-700 mb-1" />
                      <p className="text-[11px] font-mono text-gray-500">今日热点尚未生成</p>
                      <p className="text-[9px] font-mono text-gray-600 mt-1">
                        每日 06:00「全网热点每日更新」自动化自动生成，写入 Vault HotTopics
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-1 max-h-72 overflow-y-auto pr-1">
                      {hotTopics.map((t, i) => (
                        <div
                          key={i}
                          className="rounded-md border border-white/[0.04] bg-white/[0.02] px-2.5 py-1.5"
                        >
                          <p className="text-[10px] text-gray-200 leading-snug">{t.title}</p>
                          {t.angle && <p className="text-[8px] font-mono text-amber-500/70 mt-0.5">{t.angle.slice(0, 60)}</p>}
                        </div>
                      ))}
                    </div>
                  )}
                </motion.div>
              )}
            </AnimatePresence>
          </>
        )}
      </div>
    </motion.div>
  );
}
